// Typed registry mapping an ERP resource to how it is searched, displayed and
// listed. Seeded with the entities verified against the live ERP data; apps can
// `registerErpEntity({...})` at startup to extend the catalog for their own
// resources without touching this lib.

/** A column shown in the browse-list (the modal). `key` is a serializer column. */
export interface ErpGridColumn {
  key: string;
  labelDe: string;
}

/** Describes how a generic ERP resource is searched, displayed and listed. */
export interface ErpEntityConfig {
  /** REST resource path segment, e.g. `'products'` → `GET /v1/products`. */
  resource: string;
  /** Primary key column. `'id'` for every ERP resource. */
  idField: string;
  /** Column shown in the field after a pick (and cached as the display name). */
  displayField: string;
  /** Columns the type-ahead + modal search match (OR-combined). */
  searchFields: string[];
  /** Never match the bigint id column (display column is non-numeric only). */
  searchIgnoreId?: boolean;
  /** German label for the field caption / modal title. */
  labelDe: string;
  /** Columns shown in the browse list (id is always prepended automatically). */
  gridColumns: ErpGridColumn[];
}

const registry = new Map<string, ErpEntityConfig>();

/** Register (or override) an ERP entity. `idField`/`searchFields` default sensibly. */
export function registerErpEntity(cfg: {
  resource: string;
  displayField: string;
  labelDe: string;
  gridColumns: ErpGridColumn[];
  idField?: string;
  searchFields?: string[];
  searchIgnoreId?: boolean;
}): void {
  registry.set(cfg.resource, {
    resource: cfg.resource,
    idField: cfg.idField ?? 'id',
    displayField: cfg.displayField,
    searchFields: cfg.searchFields ?? [cfg.displayField],
    searchIgnoreId: cfg.searchIgnoreId,
    labelDe: cfg.labelDe,
    gridColumns: cfg.gridColumns,
  });
}

export function getErpEntity(resource: string): ErpEntityConfig | undefined {
  return registry.get(resource);
}

export const erpEntityRegistry = registry;

// --- seed: display/search fields verified against the live ERP data ---
// (matchcode is null on the seeded products — productNumber + name are the
// populated, searchable fields).

registerErpEntity({
  resource: 'products',
  displayField: 'name',
  searchFields: ['productNumber', 'name'],
  labelDe: 'Artikel',
  gridColumns: [
    { key: 'productNumber', labelDe: 'Artikelnr.' },
    { key: 'name', labelDe: 'Bezeichnung' },
  ],
});

registerErpEntity({
  resource: 'customers',
  displayField: 'name',
  searchFields: ['name'],
  labelDe: 'Kunde',
  gridColumns: [{ key: 'name', labelDe: 'Name' }],
});

registerErpEntity({
  resource: 'currencies',
  displayField: 'code',
  searchFields: ['code', 'name'],
  labelDe: 'Währung',
  gridColumns: [
    { key: 'code', labelDe: 'Kürzel' },
    { key: 'name', labelDe: 'Bezeichnung' },
  ],
});
